/* ── Tier pricing ── */
export type TierPrice = {
  qty: number;
  price: number;
};

/* ── Image from WPGraphQL ── */
export type WCImage = {
  sourceUrl: string;
  altText?: string;
};

/* ── Taxonomy term ── */
export type WCTerm = {
  name: string;
  slug: string;
};

/* ── Drug schedule with custom term meta ── */
export type WCDrugSchedule = WCTerm & {
  prescriptionRequired?: boolean;
  exportable?: boolean;
  scheduleCountry?: string;
};

/* ── Product variation attribute ── */
export type WCVariationAttribute = {
  name: string;
  value: string;
};

/* ── Product variation (WPGraphQL ProductVariation) ── */
export type WCVariation = {
  databaseId: number;
  name: string;
  price: string | null;
  regularPrice: string | null;
  salePrice: string | null;
  stockStatus: string | null;
  attributes: {
    nodes: WCVariationAttribute[];
  };
  image: WCImage | null;
  npharmPackageId: string | null;
  npharmVendorId: string | null;
  npharmVendorName: string | null;
  npharmVendorCountry: string | null;
  npharmMinQty: string | null;
  npharmMaxQty: string | null;
  npharmTierPrices: string | null;
  npharmRequiresFridge: string | null;
  npharmSpecialOrder: string | null;
  npharmHighValue: string | null;
  npharmBackorder: string | null;
};

/* ── Strength sibling — same active ingredient, different dose ── */
export type StrengthSibling = {
  databaseId: number;
  slug: string;
  strength: string | null;
  hasAvailableVariations: boolean;
};

/* ── WooCommerce product (VariableProduct / SimpleProduct) ── */
export type WCProduct = {
  databaseId: number;
  name: string;
  slug: string;
  type: string;
  shortDescription: string | null;
  description: string | null;
  image: WCImage | null;
  galleryImages?: {
    nodes: WCImage[];
  };
  productCategories: {
    nodes: WCTerm[];
  };
  medicalConditions?: {
    nodes: WCTerm[];
  };
  activeIngredients?: {
    nodes: WCTerm[];
  };
  dosageForms?: {
    nodes: WCTerm[];
  };
  drugManufacturers?: {
    nodes: WCTerm[];
  };
  drugSchedules?: {
    nodes: WCDrugSchedule[];
  };
  allSpecies?: {
    nodes: WCTerm[];
  };
  npharmDrugId: string | null;
  npharmDrugName: string | null;
  npharmStrength: string | null;
  npharmIngredientHash: string | null;
  npharmGeneric: string | null;
  npharmPronunciation: string | null;
  npharmUdn: string | null;
  strengthSiblings?: StrengthSibling[] | null;
  npharmMonograph?: {
    country: string | null;
    udn: string | null;
    sections: { suffix: string; label: string; content: string }[];
  } | null;
  /* SEO fields from Rank Math bridge */
  seo?: {
    title: string | null;
    description: string | null;
    canonical: string | null;
    focusKw: string | null;
    robots: string[] | null;
    openGraph: {
      title: string | null;
      description: string | null;
      imageUrl: string | null;
      imageWidth: number | null;
      imageHeight: number | null;
      type: string | null;
    } | null;
    jsonLd: string | null;
  } | null;
  /* Custom NPharmacy SEO fields */
  npharmSeoTags?: string | null;
  npharmSeoKeywords?: string | null;
  npharmCanonicalUrl?: string | null;
  npharmSchemaType?: string | null;
  npharmSeoJsonLd?: string | null;
  variations?: {
    nodes: WCVariation[];
  };
};

/* ── Helpers ── */

export function parseTierPrices(json: string | null | undefined): TierPrice[] {
  if (!json) return [];
  try {
    const raw = JSON.parse(json) as { qty: number | string; price: number | string }[];
    return raw
      .map((t) => ({ qty: Number(t.qty), price: Number(t.price) }))
      .filter((t) => t.qty > 0 && t.price > 0)
      .sort((a, b) => a.qty - b.qty);
  } catch {
    return [];
  }
}

export function parsePrice(priceStr: string | null | undefined): number {
  if (!priceStr) return 0;
  const cleaned = priceStr.replace(/[^0-9.]/g, "");
  return parseFloat(cleaned) || 0;
}

export function getLowestPrice(product: WCProduct): number {
  if (!product.variations?.nodes?.length) return 0;

  let lowest = Infinity;
  for (const v of product.variations.nodes) {
    const tiers = parseTierPrices(v.npharmTierPrices);
    if (tiers.length > 0) {
      const minTier = Math.min(...tiers.map((t) => t.price));
      if (minTier < lowest) lowest = minTier;
    }

    const base = parsePrice(v.price);
    if (base > 0 && base < lowest) lowest = base;
  }

  return lowest === Infinity ? 0 : lowest;
}

export function getBasePrice(variation: WCVariation): number {
  const tiers = parseTierPrices(variation.npharmTierPrices);
  if (tiers.length > 0) {
    const single = tiers.find((t) => t.qty === 1);
    if (single) return single.price;
    return tiers[0].price;
  }
  return parsePrice(variation.price);
}

export function getTierPriceForQty(
  variation: WCVariation,
  qty: number,
): number {
  const tiers = parseTierPrices(variation.npharmTierPrices);
  if (tiers.length === 0) return parsePrice(variation.price);

  let best = tiers[0];
  for (const t of tiers) {
    if (qty >= t.qty) best = t;
  }
  return best.price;
}

export const PLACEHOLDER_IMAGE = "/images/medication-placeholder.svg";

export function productImageSrc(product: WCProduct): string {
  return product.image?.sourceUrl || PLACEHOLDER_IMAGE;
}

export function variationImageSrc(variation: WCVariation, fallback: string): string {
  return variation.image?.sourceUrl || fallback;
}
